import { Box_t, Layout_t, Pallet_t } from 'blackbox';
import { Application, UnitsSystem } from 'libapi';
import { KeyboardTrigger, SelectTrigger } from '../../../Layout';
import {
	getChangeWrapperNumber,
	from_unit,
	UNITS,
	render_length_unit,
	renderInputLength,
	getChangeWrapperString,
	wrapChangeEventNumber,
	to_unit,
} from '../../../shared/shared';
import { ModelBox } from '../../layout_utils';
import { useMachineConfig } from '../../../General';
import { useContext } from 'react';
import { ApplicationContext } from '../../../../App';

export interface EditorHeaderProps {
	allBoxes: Box_t[];
	allPallets: Pallet_t[];
	editingLayout: Layout_t;
	setEditingLayout: (pLayout: Layout_t) => void;
	showAuto: boolean;
	modelBoxes: ModelBox[];
	currentPalletIndex: number;
	handlePalletSelect: (n: number) => void;
	currentBoxIndex: number;
	handleBoxSelect: (n: number) => void;
	triggerKeyboard: KeyboardTrigger;
	triggerSelect: SelectTrigger;
	gapDistance: number;
	unitsSystem: UnitsSystem;
	snapStrength: number;
	setSnapStrength: (pNewValue: number) => void;
	pSetGapDistance: (pNewValue: number) => void;
	isNewLayout: boolean;
}

export function EditorHeader({
	allPallets,
	allBoxes,
	editingLayout,
	setEditingLayout,
	currentPalletIndex,
	showAuto,
	handlePalletSelect,
	currentBoxIndex,
	modelBoxes,
	handleBoxSelect,
	triggerKeyboard,
	triggerSelect,
	gapDistance,
	unitsSystem,
	snapStrength,
	setSnapStrength,
	pSetGapDistance,
	isNewLayout,
}: EditorHeaderProps) {
	const app = useContext(ApplicationContext) as Application;
	const { lMachineConfig } = useMachineConfig(app);
	/** Maximum overhang allowance in mm. If not set in the config, the default is 50mm*/
	const MAX_OVERHANG = lMachineConfig?.MaxOverhang ?? 50;

	const overhang = isNaN(editingLayout.overhang) ? 0 : editingLayout.overhang;
	const setOverhang = (overhang: number) =>
		setEditingLayout({ ...editingLayout, overhang });

	const lGapInputHandler = getChangeWrapperNumber(function (pNewGap: number) {
			if (unitsSystem === UnitsSystem.IMPERIAL)
				pNewGap = from_unit(pNewGap, UNITS.inches);
			pSetGapDistance(pNewGap);
		}),
		lGapKeyboardTrigger = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					`Enter box gap distance (${render_length_unit(unitsSystem)})`,
					renderInputLength(gapDistance, unitsSystem),
					lGapInputHandler.FromString
				);
			}
		};
	const lNameHandlers = getChangeWrapperString(function (pNewName: string) {
			let lCp = { ...editingLayout };
			lCp.name = pNewName;
			setEditingLayout(lCp);
		}),
		lTriggerKeyboardName = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					'Enter a name for your Layout:',
					editingLayout.name,
					lNameHandlers.Direct
				);
			}
		},
		lHandleSelectPallet = function (pEvent: React.SyntheticEvent) {
			if (!triggerSelect) return;
			pEvent.preventDefault();
			triggerSelect(
				'Select Pallet',
				allPallets.map(pPallet => pPallet.name),
				handlePalletSelect,
				currentPalletIndex
			);
		},
		lHandleSelectBox = function (pEvent: React.SyntheticEvent) {
			if (!triggerSelect) return;
			pEvent.preventDefault();
			triggerSelect(
				'Select Box',
				allBoxes.map(pBox => pBox.name),
				handleBoxSelect,
				currentBoxIndex
			);
		};

	const overhangInputHandler = getChangeWrapperNumber(
		(overhangInCurrentUnits: number) => {
			const units =
				unitsSystem === UnitsSystem.IMPERIAL ? UNITS.inches : UNITS.millimeters;
			const maxOverhang = to_unit(MAX_OVERHANG, units);
			if (overhangInCurrentUnits > maxOverhang) {
				throw new Error(
					`Overhang cannot exceed ${maxOverhang.toFixed(2)}${render_length_unit(
						unitsSystem
					)}`
				);
			} else if (overhangInCurrentUnits < 0) {
				throw new Error('Overhang cannot be negative');
			}
			setOverhang(from_unit(overhangInCurrentUnits, units));
		}
	);
	const overhangInputTrigger = () => {
		if (triggerKeyboard && overhang !== undefined) {
			triggerKeyboard(
				`Enter overhang size (${render_length_unit(unitsSystem)})`,
				renderInputLength(overhang, unitsSystem),
				overhangInputHandler.FromString
			);
		}
	};

	return (
		<div className="full-size flex-col align-center">
			<FormField label="Layout Name" labelPosition="top">
				<input
					className="full-width"
					type="text"
					value={editingLayout.name}
					onChange={lNameHandlers.Event}
					onClick={lTriggerKeyboardName}
				/>
			</FormField>
			<FormField label="Pallet" labelPosition="top">
				<select
					className="full-width"
					value={currentPalletIndex}
					onChange={wrapChangeEventNumber(handlePalletSelect)}
					disabled={!isNewLayout || (!showAuto && modelBoxes.length !== 0)}
					onMouseDown={lHandleSelectPallet}
				>
					{allPallets.map((p: Pallet_t, i: number) => {
						return (
							<option key={i} value={i}>
								{p.name}
							</option>
						);
					})}
				</select>
			</FormField>
			<FormField label="Box" labelPosition="top">
				<select
					className="full-width"
					value={currentBoxIndex}
					onChange={wrapChangeEventNumber(handleBoxSelect)}
					disabled={!isNewLayout || (!showAuto && modelBoxes.length !== 0)}
					onMouseDown={lHandleSelectBox}
				>
					{allBoxes.map((b: Box_t, i: number) => {
						return (
							<option key={i} value={i}>
								{b.name}
							</option>
						);
					})}
				</select>
			</FormField>
			<FormField label="Gap Distance" labelPosition="top">
				<input
					className="full-width"
					type="number"
					value={renderInputLength(gapDistance, unitsSystem)}
					onChange={lGapInputHandler.Event}
					onClick={lGapKeyboardTrigger}
				/>
			</FormField>
			<FormField label="Snap Strength" labelPosition="top">
				<input
					value={snapStrength}
					type="range"
					id="snapRange"
					min="0"
					max="40"
					step="5"
					onChange={e => setSnapStrength(parseInt(e.target.value))}
				/>
			</FormField>
			<div className="full-width-minus-2pad margin-top">
				<div className="flex-row align-center">
					<span className="bold-font margin-left"> Overhang </span>
					<span
						style={{
							display: 'flex',
							flexGrow: 1,
							alignItems: 'baseline',
							justifyContent: 'flex-end',
						}}
					>
						<input
							type="number"
							className="margin-left"
							style={{ width: '80px', height: '90%', padding: '0px' }}
							value={to_unit(
								overhang,
								unitsSystem === UnitsSystem.IMPERIAL ? UNITS.inches : UNITS.millimeters
							).toFixed(2)}
							onChange={overhangInputHandler.Event}
							onClick={overhangInputTrigger}
						/>
						<span className="margin-left">{render_length_unit(unitsSystem)}</span>
					</span>
				</div>
				<p className="margin-top">
					Overhang also adds space to account for your box gap.
				</p>
			</div>
		</div>
	);
}

const FormField = ({
	label,
	labelPosition,
	children,
	flex = 'col',
	align = 'left',
}: {
	label: string;
	labelPosition: 'top' | 'right';
	children: React.ReactNode;
	flex?: 'col' | 'row';
	align?: 'left' | 'center' | 'right';
}) => (
	<div
		className={`full-width-minus-2pad flex-${flex} align-${align} double-margin-top double-margin-bottom`}
	>
		{labelPosition === 'top' && (
			<span className="bold-font margin-bottom">{label}</span>
		)}
		{children}
		{labelPosition === 'right' && (
			<span className="bold-font margin-left">{label}</span>
		)}
	</div>
);

const Checkbox = ({
	checked,
	onChange,
}: {
	checked: boolean;
	onChange: () => void;
}) => (
	<input
		className="container-border"
		style={{ height: '20px', width: '20px' }}
		type="checkbox"
		checked={checked}
		onChange={onChange}
	/>
);
